/**
 * The card every Connected Subscription is drawn in.
 *
 * The section shows two kinds of subscription side by side: the workspace's own
 * CMMD plan and the personal provider accounts someone signed into. They report
 * different quantities, a remaining allowance against a rate-limit percentage,
 * so their bodies differ. Their frames must not: a CMMD plan that was merely
 * styled to resemble a provider card would drift the first time either padding
 * changed, and the whole point of the section is that these are peers.
 *
 * @module components/usage/usageCard
 */
import type { ReactNode } from "react";
import { RefreshCwIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "../../lib/touchTarget";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

export function UsageCard({
  kind,
  icon,
  title,
  subtitle,
  action,
  children,
  className,
}: {
  /** Which subscription this is, so a test can assert the reading order. */
  readonly kind: string;
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <div
      className={cn("flex flex-col gap-4 rounded-lg border border-border p-4", className)}
      data-usage-card={kind}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          {icon}
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-foreground text-sm font-semibold">{title}</span>
            <span className="text-muted-foreground text-xs">{subtitle}</span>
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function UsageCardRefreshButton({
  label,
  tooltip,
  onRefresh,
  isDisabled,
  isRefreshing,
}: {
  readonly label: string;
  readonly tooltip: string;
  readonly onRefresh: () => void;
  readonly isDisabled: boolean;
  readonly isRefreshing: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            aria-label={label}
            className={cn(
              RESPONSIVE_TEXT_BUTTON_CLASS,
              "inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2 py-1",
            )}
            disabled={isDisabled || isRefreshing}
            onClick={onRefresh}
            type="button"
          >
            <RefreshCwIcon
              aria-hidden="true"
              className={cn(
                "size-3.5 text-muted-foreground/70",
                isRefreshing ? "motion-safe:animate-spin" : null,
              )}
            />
            <span className="text-xs font-medium">Refresh</span>
          </button>
        }
      />
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * The consumed portion of one allowance or window.
 *
 * Always renders its track, so a meter at zero reads as an untouched allowance
 * rather than as a row that failed to draw.
 */
export function UsageMeterBar({
  share,
  fillClassName = "bg-primary",
}: {
  /** Consumed fraction in `0..1`. */
  readonly share: number;
  readonly fillClassName?: string;
}) {
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn("h-full rounded-full", fillClassName)}
        style={{ width: `${share * 100}%` }}
      />
    </div>
  );
}
